import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { menuSize, placeMenu } from '../utils/menuPlacement'
import './glassMenu.css'

const ACTIONS = [
  { id: 'compare', label: 'Comparer', icon: '/Icon compare.png', show: (props) => props.showCompare !== false },
  { id: 'duplicate', label: 'Dupliquer', icon: '/Icon copy.png' },
  { id: 'rename', label: 'Renommer', icon: '/Icon drive file rename outline.png' },
  { id: 'delete', label: 'Supprimer', icon: '/Icon trash.png', danger: true },
]

function visibleActions(props) {
  return ACTIONS.filter((action) => (action.show ? action.show(props) : true))
}

export default function ContextMenu({
  x = 0,
  y = 0,
  onClose,
  onAction,
  showCompare = true,
}) {
  const menuRef = useRef(null)
  const reducedMotion = usePrefersReducedMotion()
  const items = visibleActions({ showCompare })
  const [activeIndex, setActiveIndex] = useState(0)
  const [coords, setCoords] = useState(() => placeMenu({
    x,
    y,
    width: 240,
    height: 168,
    vw: typeof window === 'undefined' ? 1024 : window.innerWidth,
    vh: typeof window === 'undefined' ? 768 : window.innerHeight,
  }))

  useLayoutEffect(() => {
    const node = menuRef.current
    if (!node) return
    const { width, height } = menuSize(node)
    setCoords(placeMenu({
      x,
      y,
      width,
      height,
      vw: window.innerWidth,
      vh: window.innerHeight,
    }))
  }, [x, y, items.length])

  useEffect(() => {
    const node = menuRef.current
    const focusable = node?.querySelectorAll('[role="menuitem"]')
    focusable?.[activeIndex]?.focus()
  }, [activeIndex, coords])

  useEffect(() => {
    const onPointer = (event) => {
      if (!menuRef.current?.contains(event.target)) onClose?.()
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => (index + 1) % items.length)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => (index - 1 + items.length) % items.length)
      } else if (event.key === 'Home') {
        event.preventDefault()
        setActiveIndex(0)
      } else if (event.key === 'End') {
        event.preventDefault()
        setActiveIndex(items.length - 1)
      }
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [items.length, onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div className="glass-menu-root" style={{ left: coords.left, top: coords.top }} data-testid="cart-context-menu">
      <div
        ref={menuRef}
        className={`lg-panel glass-menu${reducedMotion ? '' : ' is-animated'}`}
        role="menu"
        aria-label="Actions du panier"
      >
        <div className="lg-panel-frost" />
        <div className="lg-panel-tint" />
        <div className="lg-panel-shine" />
        <ul className="lg-panel-body glass-menu-list">
          {items.map((action, index) => (
            <li key={action.id} role="none">
              <button
                type="button"
                role="menuitem"
                className={`glass-menu-item${action.danger ? ' is-danger' : ''}${index === activeIndex ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => onAction?.(action.id)}
              >
                <img src={action.icon} alt="" className="glass-menu-icon" />
                <span className="glass-menu-label">{action.label}</span>
                <span className="glass-menu-trail" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  )
}
